import React from 'react';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import { Button } from '@this/components/shared/ui/inputs/button';
import Tooltip from '@this/shared/tooltip/tooltip';
import { digits } from '@this/src/util';
import { Link } from '@this/shared/ui/navigations/link';

import type SearchQuery from '../../../../../domain/search_query';
import type SearchQueryItem from '../../../../../domain/search_query_item';
import type SelectStore from '../../../../../domain/select_store';
import SearchBlock from '../../../../shared/search/search_block/search_block';
import OutlineItemShortDistance from '../../outline_item/OutlineItemShortDistance';
import { OutlineListRail, OutlineRailContent, Header } from '../../outline_item/outline_item.template';
import { ArrowIcon, AddButtonWrapper, AddItemLink } from './outline.template';

interface Props {
  query: SearchQuery;
  store: SelectStore;
  selectErrors: string[];
  submitting: boolean;
  handleSelectItem: () => void;
  handleAddItem?: (item_type: 'transport' | 'hotel', e: React.MouseEvent<HTMLElement>) => void;
  handleAdditionalSearch: (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => void;
  additionalSearchErrors?: { [k in string]: string };
  handleRemoveItem?: () => void;

  handleNextButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleOpenShowSearchBox: () => void;
  isRequestForm: boolean; // 旧コメント: ArrangementRequestForm
}

const OutlineShortDistanceContainer = ({
  query,
  store,
  selectErrors,
  handleSelectItem,
  handleAddItem,
  handleRemoveItem,
  handleAdditionalSearch,
  additionalSearchErrors,
  handleNextButtonClick,
  submitting,
  isRequestForm,
  handleOpenShowSearchBox
}: Props) => {
  const getDisplayItems = () => {
    if (store.showPackage && store.result.type === 'airPackage') {
      return store.result.packageItems[store.result.airline];
    }

    if (store.showPackage && store.result.type === 'jrPackage') {
      return store.result.jrPackageItems;
    }

    return store.result.items;
  };

  return (
    <StyledOutline isRequestForm={isRequestForm}>
      <OutlineListRail>
        {getDisplayItems().map((item, i) => (
          <OutlineItemShortDistance
            railLayout
            key={item.index}
            sectionNumber={i + 1}
            item={item}
            handleClickTab={store.handleClickTab}
            handleSelectItem={handleSelectItem}
            selected={store.currentTab === item.index}
            hotelPriceLimit={item.hotelPriceLimit}
            store={store}
            isRequestForm={isRequestForm}
            handleOpenShowSearchBox={handleOpenShowSearchBox}
          />
        ))}

        {store.result.type === 'separate' &&
          handleAddItem &&
          store.result.isMultipleLocationsSearchResult() &&
          (store.result.items.length === store.query.items.length ? (
            <ItemBlock className="select-outline__item-block">
              <OutlineRailContent>
                <AddButtonWrapper>
                  {store.result.transportAvailable && (
                    <AddItemLink
                      type="submit"
                      disabled={query.isItemLimit()}
                      onClick={e => handleAddItem('transport', e)}
                    >
                      +交通機関を追加
                    </AddItemLink>
                  )}
                  {store.result.hotelAvailable && (
                    <AddItemLink
                      type="submit"
                      disabled={query.isItemLimit()}
                      onClick={e => handleAddItem('hotel', e)}
                    >
                      +ホテルを追加
                    </AddItemLink>
                  )}
                </AddButtonWrapper>
              </OutlineRailContent>
            </ItemBlock>
          ) : store.result.items.length < store.query.items.length ? (
            <ItemBlock className="select-outline__item-block">
              <OutlineRailContent>
                <form
                  className="search"
                  onSubmit={e => handleAdditionalSearch(store.query.items[store.query.items.length - 1], e)}
                >
                  <SearchBlock item={store.query.items[store.query.items.length - 1]} />
                  <div className="search__button-wrapper">
                    {additionalSearchErrors && (
                      <div className="search__error">
                        {Object.keys(additionalSearchErrors).map((key, i) => (
                          <div className="error" key={i}>
                            {additionalSearchErrors[key]}
                          </div>
                        ))}
                      </div>
                    )}
                    <StyledButtonGroup>
                      <Button type="submit" disabled={submitting}>
                        検索
                      </Button>
                      <Link onClick={handleRemoveItem}>キャンセル</Link>
                    </StyledButtonGroup>
                  </div>
                </form>
              </OutlineRailContent>
            </ItemBlock>
          ) : null)}

        <ItemBlock className="select-outline__item-block">
          <Header $alignToRail $packageTotalRow={store.result.type !== 'separate'}>
            <ArrowIcon>↑</ArrowIcon>
            {store.result.type === 'separate' ? (
              <StyledDate>
                合計金額：
                <StyledPrice>
                  <span data-wovn-ignore>{digits(store.result.getReservingTrip().totalPrice())}</span>
                  <StyledYen>円</StyledYen>
                </StyledPrice>
                <Tooltip type="info">
                  <div className="select__tooltip-format">
                    {store.result
                      .getReservingTrip()
                      .tooltipText()
                      .split('\n')
                      .map((l: string, i: number) => (
                        <p key={i}>{l}</p>
                      ))}
                  </div>
                </Tooltip>
              </StyledDate>
            ) : (
              <StyledPackagePriceArea>
                <StyledWrap>
                  <StyledContent>
                    <StyledPackageTitle>
                      パッケージプラン
                      <Tooltip type="info">
                        <div className="select__tooltip-format">
                          {store.result
                            .getPackageTrip()
                            .tooltipText()
                            .split('\n')
                            .map((l: string, i: number) => (
                              <p key={i}>{l}</p>
                            ))}
                        </div>
                      </Tooltip>
                    </StyledPackageTitle>
                    <StyledPrice>
                      合計：
                      <StyledPriceMain data-wovn-ignore>
                        {digits(store.result.getPackageTrip().totalPrice())}
                      </StyledPriceMain>{' '}
                      円 (税込)
                    </StyledPrice>
                  </StyledContent>
                </StyledWrap>
              </StyledPackagePriceArea>
            )}
          </Header>
        </ItemBlock>
      </OutlineListRail>

      {isRequestForm === false && (
        <>
          <StyledReserveButton
            className={`select-pc__reserve-button ${submitting ? 'loading' : ''}`}
            onClick={handleNextButtonClick}
            disabled={submitting}
            loading={submitting}
            color="primary"
            size="medium"
          >
            予約確認画面へ
          </StyledReserveButton>
          {selectErrors.length > 0 && (
            <div className="select__errors-bottom">
              {selectErrors.map((error, i) => (
                <div className="error" key={i}>
                  {error}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </StyledOutline>
  );
};
const StyledDate = styled.div`
  display: flex;
  font-weight: bold;
  margin-right: auto;
  font-size: 16px;

  ${media.sp`
    font-size: 13px;
  `}
`;

const StyledOutline = styled.div<{ isRequestForm: boolean }>`
  padding: ${props => (props.isRequestForm ? '15px 10px' : '10px 0 60px')};
`;

const ItemBlock = styled.div`
  ${media.sp`
    padding: 0 12px 0 4px;
  `}
`;

const StyledPrice = styled.span`
  color: ${props => props.theme.redColor};
  vertical-align: text-bottom;
  display: inline;

  > span:first-of-type {
    ${media.sp`
      font-size: 18px;
      font-weight: bold;
      line-height: 1;
    `}
  }
`;

const StyledYen = styled.span`
  font-size: 9px;
  margin-left: 3px;
`;

const StyledPriceMain = styled.span`
  font-size: 18px;
`;

const StyledReserveButton = styled(Button)`
  display: none;
  position: relative;
  width: 214px;
  margin: 40px auto;
  ${media.sp`
    display: block;
  `}
`;

const StyledPackagePriceArea = styled.div`
  display: flex;
  width: 100%;
  height: 77px;
  background: #f6f4ee;
`;

const StyledPackageTitle = styled.div`
  font-size: 11px;
  display: flex;
  margin-bottom: 5px;
  justify-content: center;
  color: #222222;
`;

const StyledContent = styled.div`
  margin: auto;
`;

const StyledWrap = styled.div`
  display: flex;
  font-weight: bold;
  width: 100%;
`;

const StyledButtonGroup = styled.div`
  display: flex;
  align-items: center;

  *:not(:first-child) {
    margin-left: 10px;
  }
`;

export default OutlineShortDistanceContainer;
